import { Checkbox, CheckboxGroup, HStack, Select, Skeleton, Stack, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { Field, PartialStateCode } from 'api/types';
import i18n from 'i18next';
import { flatten, forEach, includes, isBoolean, isNil, map, omitBy, property, remove, values } from 'lodash';
import { Fragment, useCallback, useEffect, useRef } from 'react';
import { getI18n } from 'react-i18next';
import { RiArrowDownSLine, RiArrowUpDoubleFill, RiArrowUpSLine, RiCircleLine, RiEqualFill } from 'react-icons/ri';

import { LoadingSpinner } from '../components/LoadingSpinner';
import { DropdownOption } from '../components/Table/Filter';
import { ISubscriptionPlan, PLAN_KEYS } from '../hooks/useSubscriptionPlanQuery';
import BMW from '../pages/serviceRecord/makeCards/BMW';
import Ford from '../pages/serviceRecord/makeCards/Ford';
import JLR from '../pages/serviceRecord/makeCards/JLR';
import Mazda from '../pages/serviceRecord/makeCards/Mazda';
import MazdaGermany from '../pages/serviceRecord/makeCards/MazdaGermany';
import Mercedes from '../pages/serviceRecord/makeCards/Mercedes';
import Standard from '../pages/serviceRecord/makeCards/Standard';
import Toyota from '../pages/serviceRecord/makeCards/Toyota';
import VW from '../pages/serviceRecord/makeCards/VW';
import { dateTimeTransformer } from './dateTransformer';

// Returns the priority map array with translation
export const getPriorityMap = (t: any) => [
  { value: 100, icon: <RiArrowUpDoubleFill size={26} color="red" />, text: t('tasks:task_priority.urgent') },
  { value: 50, icon: <RiArrowUpSLine size={26} color="red" />, text: t('tasks:task_priority.high') },
  { value: 0, icon: <RiEqualFill size={24} color="orange" />, text: t('tasks:task_priority.medium') },
  { value: -50, icon: <RiArrowDownSLine size={26} color="blue" />, text: t('tasks:task_priority.low') },
  { value: -100, icon: <RiCircleLine size={18} color="gray" />, text: t('tasks:task_priority.non_important') },
];

export const getKeyByValue = (object: object, value: any) => {
  // @ts-ignore
  return Object.keys(object).find((key) => object[key] === value);
};
export const getStatus = (statusCodes: object, status: string) => {
  // @ts-ignore
  return parseInt(getKeyByValue(statusCodes, status) as string);
};
export const getReason = (reasonCodes: object, reasonParent: number, reason: string) => {
  // @ts-ignore
  const reasonObject = reasonCodes[reasonParent] || {};
  // @ts-ignore
  return parseInt(getKeyByValue(reasonObject, reason));
};
export const getArrayForGroupedDropdown = (options: any) => {
  return values(omitBy(flatten(map(options, property('options'))), isNil));
};

export const displayRow = (props: { key?: any; label?: any; content: any; contentOnNewLine?: boolean; isLoading?: boolean; noColon?: boolean }) => {
  let Wrapper = HStack;
  const { contentOnNewLine, content, label = false, isLoading, key, noColon = false } = props;
  if (contentOnNewLine) {
    Wrapper = Stack;
  }
  if (isLoading) {
    return <Skeleton h="40px" speed={2} fadeDuration={8} />;
  }
  return (
    <Wrapper key={key} alignItems={'flex-start'}>
      {label && (
        <Text fontSize="sm" fontWeight="semibold">
          {label}
          {!noColon && ':'}
        </Text>
      )}

      <Text fontSize="sm">{content}</Text>
    </Wrapper>
  );
};

export const displayColumn = (props: {
  key?: any;
  label?: string;
  content: any;
  contentOnNewLine?: boolean;
  isLoading?: boolean;
  noColon?: boolean;
}) => {
  let Wrapper = HStack;
  const { contentOnNewLine, content, label = false, isLoading, key, noColon = false } = props;
  if (contentOnNewLine) {
    Wrapper = Stack;
  }
  if (isLoading) {
    return <Skeleton h="40px" speed={2} fadeDuration={8} />;
  }

  return (
    <Wrapper key={key} alignItems={'flex-start'}>
      {label && (
        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold">
          {label}
          {!noColon && ':'}
        </Text>
      )}
      <VStack justifyContent="center" alignItems="flex-start">
        {content &&
          content?.map((text: string, index: number) => (
            <Text fontSize="sm" overflowWrap={'break-word'} wordBreak={'break-word'} key={index}>
              {text}
            </Text>
          ))}
      </VStack>
    </Wrapper>
  );
};

export function useClickOutsideTag(onClick: { (): void; (): void }, tag: string) {
  const ref = useRef(null);

  const handleClickOutside = useCallback(
    () => (event: { target: { tagName: any } }) => {
      if (ref.current && event.target.tagName !== tag) {
        onClick();
      }
    },
    [onClick, tag],
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
}

export function addRemoveValueFromArray(searchArray: Array<any>, value: any) {
  if (!includes(searchArray, value)) {
    searchArray.push(value);
  } else {
    remove(searchArray, value);
  }
  return searchArray;
}

export const createDropdownPlainOptions = (props: {
  records: Array<any>;
  valueColumn: string;
  labelColumn: string;
  initialOptions?: DropdownOption;
}): DropdownOption[] => {
  const { records = [], valueColumn, labelColumn, initialOptions = { label: i18n.t('common:all'), value: '' } } = props;
  let returnArray = [];

  if (initialOptions) {
    returnArray.push(initialOptions);
  }

  forEach(records, (value) => {
    returnArray.push({
      label: value[labelColumn],
      value: value[valueColumn],
    });
  });
  return returnArray;
};

export const createDropdownStaticOptionsWithMutator = (props: {
  records: Array<any>;
  mutator: (value: any) => any;
  initialOptions?: DropdownOption;
  t: any;
}): DropdownOption[] => {
  const { records = [], mutator } = props;
  let returnArray: DropdownOption[] = [];

  forEach(records, (value) => {
    returnArray.push({
      label: mutator(value),
      value: value,
    });
  });
  return returnArray;
};

export const createDropdownTranslatedOptions = (props: {
  records: any;
  translationKey: string;
  valueColumn: string;
  labelColumn: string;
  initialOptions?: object;
}) => {
  const { records, translationKey, valueColumn, labelColumn, initialOptions = { label: i18n.t('common:choose'), value: 'unknown' } } = props;
  let returnArray = [];

  if (initialOptions) {
    returnArray.push(initialOptions);
  }

  forEach(records, (value) => {
    returnArray.push({
      label: i18n.t(`${translationKey}:${value[valueColumn]}`),
      value: value[labelColumn],
    });
  });
  return returnArray;
};

export const createStatusDropdown = (props: { statuses: any; translationKey: string; initialOptions?: object }) => {
  const { statuses = {}, translationKey } = props;
  let returnArray: Array<any> = [];

  forEach(statuses, (value) => {
    returnArray.push({
      label: i18n.t(`${translationKey}:status.${value}.label`),
      value: getStatus(statuses, value),
    });
  });
  return returnArray;
};

export const createDropdownTranslatedStaticOptions = (props: {
  records: any;
  translationKey: string;
  initialOptions?: DropdownOption;
}): DropdownOption[] => {
  const { records, translationKey, initialOptions = { value: '', label: i18n.t('common:all') } } = props;
  let returnArray = [];

  if (initialOptions && Object.keys(initialOptions).length > 0) {
    returnArray.push(initialOptions);
  }

  forEach(records, (value) => {
    returnArray.push({
      label: i18n.t(`${translationKey}.${value}`),
      value: value,
    });
  });
  return returnArray;
};

export const renderValue = (value: any, fieldConfig: Field) => {
  if (isBoolean(value)) {
    return value;
  }
  if (fieldConfig?.display?.type === 'date') {
    //dates will make the view crash, so they need to be formatted
    return dateTimeTransformer(value);
  }
  if (fieldConfig?.display?.type === 'checkbox') {
    //we only display checked values so they need to be true
    return i18n.t('common:yes');
  }
  const displayType = fieldConfig?.display?.type;
  if ((displayType === 'dropdown' || displayType === 'radio') && fieldConfig?.type === 'string') {
    if (value.includes('/')) {
      const chunks = value.split('/');
      return `${chunks[0]}. ${i18n.t('forms:year.label')} / ${chunks[1].toLocaleString('de-DE')} km`;
    } else {
      return fieldConfig?.display?.options?.find((obj: DropdownOption) => obj.value === value)?.label ?? value;
    }
  }

  return value;
};

export const statusFilter = (
  status: Array<string>,
  setStatus: (value: Array<string>) => void,
  translationKey: string,
  codes: PartialStateCode<string>,
  allStatusSelected: boolean,
) => {
  return (
    <Stack spacing={[1, 5]} flexWrap={'wrap'} direction={['column', 'row']}>
      <Checkbox
        onChange={() => {
          if (allStatusSelected) {
            setStatus([]);
          } else {
            setStatus(Object.keys(codes));
          }
        }}
      />
      <Text as={'div'} color="muted" fontSize="xs" m={0}>
        <> {getI18n().t(`components:table.filters.${allStatusSelected ? 'deselect_all' : 'select_all'}`)}</>
      </Text>
      <CheckboxGroup defaultValue={status} onChange={setStatus}>
        {Object.keys(codes).map((code: string) => {
          return (
            <Fragment key={code}>
              <Checkbox value={code} data-test-id={code} />
              <Text as={'div'} color="muted" fontSize="xs" m={0}>
                {getI18n().t(`${translationKey}:status.${codes[code]}.label`)}
              </Text>
            </Fragment>
          );
        })}
      </CheckboxGroup>
    </Stack>
  );
};

export function getComponentBasedOnType(type: any) {
  if (!type) {
    return <LoadingSpinner />;
  }
  const typeMapping = {
    'vw-group': VW,
    bmw: BMW,
    'bmw-m': Standard,
    ford: Ford,
    jlr: JLR,
    mazda: Mazda,
    'mazda-germany': MazdaGermany,
    mercedes: Mercedes,
    'mercedes-commercial-vehicles': Mercedes,
    smart: Mercedes,
    toyota: Toyota,
    lexus: Standard,
    hyundai: Standard,
    idsb: Standard,
    legacy: Standard,
  };
  // @ts-ignore
  return typeMapping[type] ?? Standard;
}
export function hasPlan(plan: ISubscriptionPlan | undefined, planId: string): boolean {
  if (isNil(plan?.id)) {
    return false;
  }
  return plan?.id === planId;
}
export const isPayPerUse = (plan: ISubscriptionPlan | undefined) =>
  hasPlan(plan, PLAN_KEYS.PAY_PER_USE_PLAN) || hasPlan(plan, PLAN_KEYS.PAY_PER_USE_PLAN_LEGACY);

export const PlainDropdown = (props: {
  ariaLabel?: string;
  value?: string | number | boolean;
  disabled?: boolean;
  onChange: any;
  options: any[];
}) => {
  const { ariaLabel = '', options, onChange, value, disabled = false } = props;

  return (
    <Select
      bgColor={'white'}
      disabled={disabled}
      defaultValue={''}
      w={'fit-content'}
      aria-label={ariaLabel}
      value={options.find((c) => c.value === value)?.value}
      onChange={(e) => onChange(e.target.value)}
      errorBorderColor={useColorModeValue('status.error', 'status.error')}
      focusBorderColor={useColorModeValue('accent', 'accent')}
    >
      {options.map((option, index) => (
        <option key={index} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );
};

export const formatArrayToJSONObject = (data: Array<string>) => {
  const returnData = {};
  data.forEach((row: string) => {
    // @ts-ignore
    returnData[row] = true;
  });
  return returnData;
};
